import { useCallback, useMemo, useRef, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { useLazyFetchBrokersQuery } from '@api/Trinium/Endpoints/Broker/slice';
import { useLazyFetchDriversQuery } from '@api/Trinium/Endpoints/Driver/slice';
import { FormikAutocomplete } from '@forms/Components/AutocompleteInput';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import useDebouncer from '@hooks/useDebounce';
import { FormikButtonDropdown } from '@forms/Components/Dropdowns/ButtonDropdown';
import DriverButtonContent from '../Driver/ButtonContent';
import DriverListButtonContent from '../Driver/ButtonListContent';
import { AssignedDriver } from '@forms/Driver/types';
import classNames from 'classnames/bind';
import { DriverFieldProps, BaseDriverFieldProps } from './Driver.types';
import Actions from '@forms/Driver/Actions';

import styles from '../Driver/ButtonContent.module.css';
const classNameCtx = classNames.bind(styles);

export default function DriverField(props: DriverFieldProps) {
  const { getFieldMeta } = useFormikContext();
  const { value } = getFieldMeta<AssignedDriver | null>(props.fieldName);

  const componentClasses = classNameCtx({
    'button-field-container': true,
    [props.className ?? '']: Boolean(props.className)
  });

  /**
   * Memoize the component returned by FormikButtonDropdown so the reference stays stable
   * across re-renders. Without this, every re-render of DriverField creates a new component
   * function, causing React to unmount/remount the inner ButtonDropdown and reset its
   * anchorEl state — which closes the popup.
   */
  const modeRef = useRef(props.mode);
  modeRef.current = props.mode;
  const valueRef = useRef(value);
  valueRef.current = value;

  const DriverDropdownComponent = useMemo(
    () =>
      FormikButtonDropdown({
        buttonConfig: {
          className: styles['assgn-driver-btn']
        },
        ButtonContent: DriverButtonContent,
        buttonContentProps: {
          get assigned() {
            return valueRef.current;
          },
          get disableVehicleInfo() {
            return modeRef.current.driverOnly;
          }
        },
        PopupContent: DriverListButtonContent,
        popupContentProps: {
          get mode() {
            return modeRef.current;
          },
          className: styles['driver-list-popup']
        }
      }),
    // Static deps only — dynamic values are accessed via refs to keep the component reference stable
    []
  );

  return (
    <div className={componentClasses}>
      <Field
        name={props.fieldName}
        label={props.label}
        component={DriverDropdownComponent}
        required={props.required}
        disabled={props.disabled}
        clearable={value?.driver?.id && props.clearable}
      />
      {props.mode.renderActionProps && <Actions {...props.mode.renderActionProps} />}
    </div>
  );
}

export function DriverAutocompleteField({ fieldName, className }: BaseDriverFieldProps) {
  const { number: terminalNumber } = useCurrentTerminal();
  const { getFieldMeta } = useFormikContext();
  const { value } = getFieldMeta<string>(fieldName);

  const [fetchBrokers, { brokers, fetchingBrokers }] = useLazyFetchBrokersQuery({
    selectFromResult: ({ data, isFetching }) => ({
      /** Because we are using the URL Parameter, we can only support the string.
       * Using a complex object would require making an API call to get the Broker object
       * before being able to plug into the autocomplete input.
       */
      brokers: data ? data.map((broker) => broker.name) : [],
      fetchingBrokers: isFetching
    })
  });

  const [fetchDrivers, { drivers, fetchingDrivers }] = useLazyFetchDriversQuery({
    selectFromResult: ({ data, isFetching }) => ({
      /** Because we are using the URL Parameter, we can only support the string.
       * Using a complex object would require making an API call to get the Driver object
       * before being able to plug into the autocomplete input.
       */
      drivers: data ? data.map((driver) => driver.name) : [],
      fetchingDrivers: isFetching
    })
  });

  const driverItems = useMemo(() => {
    return drivers.length > 0 || brokers.length > 0 ? [...drivers, ...brokers] : value ? [value] : [];
  }, [drivers, brokers, value]);

  const [debouncedDriverValue, setDebouncedDriverValue] = useState<string | null>(null);
  useDebouncer(
    () => {
      if (debouncedDriverValue && debouncedDriverValue.length > 2) {
        void fetchBrokers({
          terminalNumber,
          startsWith: debouncedDriverValue
        });

        void fetchDrivers({
          terminalNumber,
          startsWith: debouncedDriverValue
        });
      }
    },
    [debouncedDriverValue, terminalNumber, fetchBrokers, fetchDrivers],
    500
  );

  /**
   * Callback for the on change event upon keystroke
   */
  const onChangeExtension = useCallback(
    (value: string | null) => {
      setDebouncedDriverValue(value);
    },
    [setDebouncedDriverValue]
  );

  return (
    <Field
      name={fieldName}
      label='Driver/Broker Carrier'
      placeholder='Enter Driver/Broker Carrier Name'
      component={FormikAutocomplete}
      autoCompleteItems={driverItems}
      isLoading={fetchingBrokers || fetchingDrivers}
      className={className}
      onChangeExtension={onChangeExtension}
    />
  );
}
